/**
 * Sentry — Crash & Error Observability
 *
 * Captures:
 * - Frontend exceptions (React errors, unhandled rejections)
 * - 401 floods (auth injection failures)
 * - Voice pipeline exceptions
 * - Route/mount/runtime errors
 *
 * Breadcrumbs include:
 * - Current room
 * - Current voice state
 * - Current API route
 * - Owner input arrival during speech
 *
 * Setup:
 *   1. Create a Sentry project at sentry.io
 *   2. Add DSN to watchtower-ui/.env:  VITE_SENTRY_DSN=https://xxx@xxx.ingest.sentry.io/xxx
 *   3. Restart Vite / rebuild
 */

import * as Sentry from "@sentry/react";
import { desktopTrustKey } from "./desktopTrust";

const DSN = import.meta.env.VITE_SENTRY_DSN || "";

let _initialized = false;

export function initSentry(): void {
  if (_initialized) return;
  if (!DSN) {
    console.log("[Sentry] No DSN configured — running without error reporting. Add VITE_SENTRY_DSN to .env");
    return;
  }

  Sentry.init({
    dsn: DSN,
    environment: import.meta.env.DEV ? "development" : "production",
    release: (window as unknown as Record<string, unknown>).__GIT_SHA__ as string || "unknown",

    // Performance — sample 20% of transactions in prod, 100% in dev
    tracesSampleRate: import.meta.env.DEV ? 1.0 : 0.2,

    // Only send errors, not info/debug breadcrumbs from noisy libs
    beforeBreadcrumb(breadcrumb) {
      // Filter out noisy fetch breadcrumbs for non-error responses
      if (breadcrumb.category === "fetch" && breadcrumb.data) {
        const status = breadcrumb.data.status_code as number;
        if (status && status >= 200 && status < 400) return null;
      }
      return breadcrumb;
    },

    // Tag every event with Lynx context
    initialScope: {
      tags: {
        surface: (window as unknown as Record<string, unknown>).lynxDesktop ? "desktop" : "mobile-web",
        app: "watchtower-ui",
      },
    },

    // Don't send PII
    sendDefaultPii: false,

    // Ignore known non-actionable errors
    ignoreErrors: [
      // Workbox/SW noise
      "Failed to fetch",
      "Load failed",
      // ResizeObserver noise (browser bug, not our code)
      "ResizeObserver loop",
      // Extension injection
      /^chrome-extension:\/\//,
    ],
  });

  _initialized = true;
  console.log("[Sentry] Initialized — error reporting active");
}

// ─── Breadcrumb Helpers ──────────────────────────────────────

/**
 * Record current room as Sentry breadcrumb.
 * Call on every room navigation.
 */
export function breadcrumbRoom(roomId: string, roomLabel: string): void {
  if (!_initialized) return;
  Sentry.addBreadcrumb({
    category: "navigation",
    message: `Room: ${roomLabel} (${roomId})`,
    level: "info",
    data: { roomId, roomLabel },
  });
  Sentry.setTag("room", roomId);
}

/**
 * Record voice state change as breadcrumb.
 * Call on every voiceStore state transition.
 */
export function breadcrumbVoiceState(
  fromState: string,
  toState: string,
  detail?: string,
): void {
  if (!_initialized) return;
  Sentry.addBreadcrumb({
    category: "voice",
    message: `Voice: ${fromState} → ${toState}${detail ? ` (${detail})` : ""}`,
    level: toState.includes("error") || toState === "deadman_killed" ? "error" : "info",
    data: { fromState, toState, detail },
  });
  Sentry.setTag("voice_state", toState);
}

/**
 * Record API call failure as breadcrumb.
 * Call on 401/4xx/5xx responses.
 */
export function breadcrumbApiError(
  method: string,
  path: string,
  status: number,
  detail?: string,
): void {
  if (!_initialized) return;
  Sentry.addBreadcrumb({
    category: "api",
    message: `${method} ${path} → ${status}`,
    level: status === 401 ? "warning" : "error",
    data: { method, path, status, detail },
  });

  // 401 flood detection — capture as explicit event if many in short window
  if (status === 401) {
    _track401(path);
  }
}

/**
 * Record owner input arrival during Zion speech.
 */
export function breadcrumbOwnerInputDuringSpeech(transcript: string): void {
  if (!_initialized) return;
  Sentry.addBreadcrumb({
    category: "voice",
    message: `Owner spoke during Zion speech: "${transcript.slice(0, 60)}"`,
    level: "warning",
    data: { transcript: transcript.slice(0, 120) },
  });
}

/**
 * Record voice pipeline failure as Sentry event (not just breadcrumb).
 */
export function captureVoiceFailure(
  stage: string,
  reason: string,
  turnData?: Record<string, unknown>,
): void {
  if (!_initialized) return;
  Sentry.captureMessage(`Voice pipeline failure: ${stage}`, {
    level: "error",
    tags: { voice_stage: stage },
    extra: { reason, ...turnData },
  });
}

// ─── 401 Flood Detection ─────────────────────────────────────

let _401count = 0;
let _401windowStart = 0;
const FLOOD_WINDOW_MS = 10_000;  // 10 seconds
const FLOOD_THRESHOLD = 5;       // 5+ 401s in 10s = flood

function _track401(path: string): void {
  const now = Date.now();
  if (now - _401windowStart > FLOOD_WINDOW_MS) {
    _401count = 0;
    _401windowStart = now;
  }
  _401count++;

  if (_401count === FLOOD_THRESHOLD) {
    Sentry.captureMessage("401 Auth Flood detected", {
      level: "error",
      tags: { issue_type: "auth_flood" },
      extra: {
        count: _401count,
        windowMs: FLOOD_WINDOW_MS,
        lastPath: path,
        hasToken: !!localStorage.getItem("watchtower_token"),
        hasZionKey: !!desktopTrustKey(),
        isDesktop: !!(window as unknown as Record<string, unknown>).lynxDesktop,
      },
    });
  }
}

// ─── Re-exports for convenience ──────────────────────────────

export { Sentry };
